import { analytics, ConfiguredDataDefinition, ConfiguredDataDefinitionFactory, Messages, InputDataDefinitionsRepository, Metric, AggregateMetric, RawMetric } from '@/apps/timeSeriesViewer';
import { Result } from '@/gr/common/result';

export class InputDataDefinitionsStore {
  constructor(
    private _configuredDataDefinitions: KnockoutObservableArray<ConfiguredDataDefinition>,
    private _configuredDataDefinitionFactory: ConfiguredDataDefinitionFactory,
    private _isLoading: KnockoutObservable<boolean>,
    private _messages: Messages.Args,
    private _repo: InputDataDefinitionsRepository
  ) {}

  setSelected(input: Metric, value: boolean): Promise<Result<undefined, undefined>> {
    if (value) {
      return this.add(input);
    } else {
      this.remove(input);
      return Promise.resolve(Result.success());
    }
  }

  private async add(id: Metric) {
    // Don't do anything for nonsensical aggregations
    if (id instanceof AggregateMetric && id.nonsensical) {
      return Result.success();
    }

    const configuredDataDefinitions = this.getConfiguredFromInput(id);
    if (configuredDataDefinitions.length > 0) return Result.success();

    analytics.stepEvent({ Action: 'AddedRawDataDefinition', RawDataDefinition: id.toUniqueString() });
    this._isLoading(true);

    const result = await this._repo.getFromId(id);
    this._isLoading(false);

    return result
      .mapSuccess((input) => {
        const newConfigured = this._configuredDataDefinitionFactory.createFromInput(input);

        // strip tooltip and nonsensical from dto as it isn't needed to be saved
        if (newConfigured.input.id instanceof RawMetric || newConfigured.input.id instanceof AggregateMetric) {
          newConfigured.input.id.tooltipInfo = undefined;
          if (newConfigured.input.id instanceof AggregateMetric) {
            newConfigured.input.id.nonsensical = undefined;
          }
        }

        this._configuredDataDefinitions.push(newConfigured);
        return Result.success();
      })
      .mapError((error) => {
        this._messages.add(error);
        return Result.error();
      });
  }

  async addChildren(id: Metric) {
    analytics.stepEvent({ Action: 'AddedChildrenOfAggregateDataDefinition', AggregateDataDefinition: id.toUniqueString() });

    this._isLoading(true);
    const result = await this._repo.getCollectionOfChildrenFromId(id);
    this._isLoading(false);

    result.ifSuccess(async (inputs) => {
      // this iterates synchronously
      for (const input of inputs) {
        if (!this.getConfiguredFromInput(input.id).length) {
          await this.add(input.id);
        }
      }
    });
  }

  remove(id: Metric): void {
    const configuredDataDefinitions = this.getConfiguredFromInput(id);
    if (configuredDataDefinitions.length === 0) return;
    analytics.stepEvent({ Action: 'RemovedInputDataDefinition', Id: id.toUniqueString() });
    this._configuredDataDefinitions.removeAll(configuredDataDefinitions);
  }

  async removeChildren(id: Metric) {
    analytics.stepEvent({ Action: 'RemoveedChildrenOfAggregateDataDefinition', AggregateDataDefinition: id.toUniqueString() });

    this._isLoading(true);
    const result = await this._repo.getCollectionOfChildrenFromId(id);
    this._isLoading(false);

    result.ifSuccess((inputs) => {
      inputs.forEach((input) => this.remove(input.id));
    });
  }

  async toggleChildren(id: Metric) {
    analytics.stepEvent({ Action: 'ToggleChildrenOfAggregateDataDefinition', AggregateDataDefinition: id.toUniqueString() });

    this._isLoading(true);
    const result = await this._repo.getCollectionOfChildrenFromId(id);
    this._isLoading(false);

    result.ifSuccess(async (inputs) => {
      // this iterates synchronously
      for (const input of inputs) {
        if (this.getConfiguredFromInput(input.id).length) {
          this.remove(input.id);
        } else {
          await this.add(input.id);
        }
      }
    });
  }

  private getConfiguredFromInput(id: Metric) {
    return this._configuredDataDefinitions().filter((d) => d.input.id.equals(id));
  }
}
